import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState('');

  const sessionId = searchParams.get('session_id');
  const productId = searchParams.get('product');

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;

    if (attempts >= maxAttempts) {
      setPaymentStatus('error');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        
        // Set download URL based on product
        if (productId === 'licensing-guide') {
          setDownloadUrl(`${BACKEND_URL}/api/download/licensing-guide`);
        } else if (productId === 'referral-database') {
          setDownloadUrl(`${BACKEND_URL}/api/download/master-referral-list`);
        }
        
        setLoading(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-white text-xl">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'error') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-900 rounded-2xl p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-3xl font-bold text-white mb-4">Payment Verification Error</h1>
          <p className="text-gray-300 mb-6">
            We couldn't verify your payment. Please check your email for confirmation or contact support.
          </p>
          <Link
            to="/"
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-full font-bold hover:bg-blue-700 transition-all"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-center border-2 border-green-500">
        <div className="text-green-500 text-6xl mb-4">✓</div>
        <h1 className="text-4xl font-bold text-white mb-4">Payment Successful!</h1>
        <p className="text-gray-300 text-lg mb-8">
          Thank you for your purchase. Your payment has been processed successfully.
        </p>

        {downloadUrl && (
          <div className="bg-blue-900/30 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Download Your Product</h2>
            <a
              href={downloadUrl}
              className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white px-10 py-4 rounded-full font-bold hover:from-blue-700 hover:to-blue-800 transition-all text-lg uppercase tracking-wide shadow-xl"
              download
            >
              Download Now →
            </a>
            <p className="text-gray-400 text-sm mt-4">
              A copy of the download link has also been sent to your email.
            </p>
          </div>
        )}

        <Link
          to="/"
          className="inline-block bg-gray-700 text-white px-8 py-3 rounded-full font-bold hover:bg-gray-600 transition-all"
        >
          Return to Home
        </Link>
      </div>
    </div>
  );
};

export default PaymentSuccess;
